// src/pages/CourseTime/CourseTime.page.jsx
import React, { useEffect, useMemo, useCallback, useRef, useState } from 'react';
import { withRouter } from "react-router";
import { useLocation, useParams } from "react-router-dom";
import { useGlobalState } from "../../context/global_state";

// Import styles
import * as S from './styles';

// Import components
import { CourseSidebar, SlideViewer, ProgressBar, DiscussionModal, FloatingChatButton, ActivityModal, FloatingActivityButton, ErrorDialog, resolveSlideTitle } from './components';

// Import hooks
import { useSessionData, useActivityInteractions, useSlideNavigation, useDiscussion, useActivityWidget } from './hooks';

// Import utilities
import { getSegmentLabel, setupPositionTracking } from './utils';
import { getActiveThemeColor, getTTSLanguageCode } from '../../utils/utils';
import { FEATURE_FLAGS, LOCAL_STORAGE_KEYS } from '../../utils/constants';

// Import services
import { finishCourseAssessmentsService, finishSessionAssessmentsService, fetchLearningProgramContent } from '../../services/learning_program.service';
import { sortSessionContent } from './utils/sortSegments';
import { deriveSlideLayouts } from './utils/deriveSlideLayout';

// Import localization
import { GET_APP_LOCALIZE_TEXT } from '../../utils/dq_lc_service/lc_service';

const CourseTime = ({ history }) => {
  const { identifier } = useParams();
  const page_location = useLocation();
  const pageLc = page_location.pathname.split('/')[1];
  const { profile, setProfile, setToastShow, setToastMessage } = useGlobalState();

  // State to control segment completion popup
  const [showSegmentCompletionPopup, setShowSegmentCompletionPopup] = useState(false);

  // State to store assessment results
  const [assessmentResults, setAssessmentResults] = useState(null);
  const [assessmentScore, setAssessmentScore] = useState(null);
  const [assessmentResultLoading, setAssessmentResultLoading] = useState(false);

  // Error dialog state
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorDialogMessage, setErrorDialogMessage] = useState('');


  // ===== TTS state =====
  const [speaking, setSpeaking] = useState(false);
  const [paused, setPaused] = useState(false);
  const [ttsAuto, setTtsAuto] = useState(false);       // Auto read across slides
  const [currentWordIndex, setCurrentWordIndex] = useState(-1);

  const utteranceRef = useRef(null);
  const speechWordsRef = useRef([]);
  const speechPartRangesRef = useRef({});
  const lastSpokenSlideTitleRef = useRef(null);        // for "skip same title"

  // TTS settings state
  const [ttsRate, setTtsRate] = useState(1);
  const [ttsPitch, setTtsPitch] = useState(1);
  const [ttsVolume, setTtsVolume] = useState(1);
  const [availableVoices, setAvailableVoices] = useState([]);
  const [selectedVoiceURI, setSelectedVoiceURI] = useState('');
  const TTS_TOAST_WARNING = GET_APP_LOCALIZE_TEXT(pageLc, 'ct__tts_browser_warning');


  // Use custom hooks for data management
  const sessionData = useSessionData(identifier, profile);
  const {
    loading,
    layoutLoading,
    sessionLoading,
    programs,
    sessionContent,
    sloTitles,
    courseTitle,
    activeSessionNo,
    activeSegment,
    positionUpdateError,
    sessionAssessments,
    updateCurrentPosition,
    markSessionCompleted,
    markSessionCompletedLocal,
    handleSessionSelect,
    positionUpdateIntervalRef,
    lastPositionUpdateRef,
    loadedSessionContentRef,
    discussionSessions,
    updateSessionData
  } = sessionData;

  // Use discussion hook first (needed by activity interactions)
  const discussionData = useDiscussion(
    identifier, 
    profile?._id, 
    sessionData.lastMessageReadTimestamp,
    sessionData.updateLastMessageReadTimestamp,
    discussionSessions
  );

  // Use activity widget hook
  const activityWidgetData = useActivityWidget(identifier, profile, pageLc);

  // Use activity interactions hook
  const activityData = useActivityInteractions(
    identifier,
    profile,
    activeSessionNo,
    activeSegment,
    sessionData.currentSlideIndex,
    sessionContent,
    discussionData.openModal,
    updateSessionData,
    discussionData.openModalWithNewSession
  );

  // Function to show styled notification instead of alert
  const showRequiredActivityNotification = (message) => {
    setToastMessage(message);
    setToastShow(true);
  };
  
  // Helper function to check if assessment has valid answer
  const checkAssessmentHasAnswer = (slide) => {
    if (!slide || slide.SlideLayout !== 'ASSESSMENT_ACTIVITY') return true;
    
    const answersMap = activityData.assessmentAnswers || {};
    const slideId = slide.SlideID;
    const currentAnswer = answersMap[slideId];
    
    if (!currentAnswer && currentAnswer !== 0) return false;
    
    if (Array.isArray(currentAnswer)) {
      return currentAnswer.length > 0;
    }
    
    if (typeof currentAnswer === 'string') {
      return currentAnswer.trim().length > 0;
    }
    
    if (typeof currentAnswer === 'object' && currentAnswer.customInput) {
      return currentAnswer.customInput.trim().length > 0;
    }
    
    return true;
  };
  
  // Check if next button should be disabled
  const isNextButtonDisabled = () => {
    if (!sessionContent || !activeSegment || !Array.isArray(sessionContent[activeSegment])) {
      return false;
    }
    
    const currentSlide = sessionContent[activeSegment][sessionData.currentSlideIndex];
    if (!currentSlide) return false;
    
    // Check if it's a required assessment without an answer
    if (currentSlide.SlideLayout === 'ASSESSMENT_ACTIVITY') {
      const config = activityData.getActivityConfig(currentSlide);
      const isRequired = config?.required || false;
      const isCompleted = activityData.activityCompleted && activityData.activityCompleted[currentSlide.SlideID];
      
      if (isRequired && !isCompleted && !checkAssessmentHasAnswer(currentSlide)) {
        return true;
      }
    }
    
    return false;
  };
  
  // Handler to auto-submit assessments before navigation
  const handleBeforeNavigate = async (currentSlide) => {
    // Check if current slide is an assessment activity
    if (currentSlide && currentSlide.SlideLayout === 'ASSESSMENT_ACTIVITY') {
      const config = activityData.getActivityConfig(currentSlide);
      const isRequired = config?.required || false;
      const isCompleted = activityData.activityCompleted && activityData.activityCompleted[currentSlide.SlideID];
      
      // If already completed, allow navigation
      if (isCompleted) {
        return true;
      }
      
      // Assessment is not completed, check if there's an answer to submit
      const answersMap = activityData.assessmentAnswers || {};
      const slideId = currentSlide.SlideID;
      const currentAnswer = answersMap[slideId];
      
      // Check if there's a valid answer to submit
      const hasValidAnswer = () => {
        if (!currentAnswer && currentAnswer !== 0) return false;
        
        if (Array.isArray(currentAnswer)) {
          return currentAnswer.length > 0;
        }
        
        if (typeof currentAnswer === 'string') {
          return currentAnswer.trim().length > 0;
        }
        
        if (typeof currentAnswer === 'object' && currentAnswer.customInput) {
          return currentAnswer.customInput.trim().length > 0;
        }
        
        return true;
      };
      
      if (!hasValidAnswer()) {
        // No valid answer provided
        if (isRequired) {
          // Required but no answer - prevent navigation
          // Don't show toast here, button should be greyed out
          return false;
        } else {
          // Not required and no answer - allow navigation
          return true;
        }
      }
      
      // We have a valid answer, submit it with loader
      try {
        await activityData.handleAssessmentSubmit(currentSlide, answersMap);
        return true;
      } catch (error) {
        showRequiredActivityNotification(GET_APP_LOCALIZE_TEXT(pageLc, 'ct__failed_submit_assessment'));
        return false;
      }
    }
    
    return true; // Allow navigation for non-assessment slides
  };
  
  // Use slide navigation hook
  const navigationData = useSlideNavigation(
    sessionContent,
    activeSegment,
    activeSessionNo,
    sessionData.currentSlideIndex,
    sessionData.setCurrentSlideIndex,
    updateCurrentPosition,
    activityData.resetActivityState,
    markSessionCompleted,
    programs,
    // Activity validation
    activityData.isActivityRequired,
    activityData.activityCompleted,
    showRequiredActivityNotification,
    // Assessment auto-submit handler
    handleBeforeNavigate,
    // Segment completion popup control
    setShowSegmentCompletionPopup,
    // Guards auto-completion against the session-switch race
    sessionData.loadedSessionContentRef,
    sessionData.sessionLoading
  );

  // Get theme color
  const themeColor = getActiveThemeColor(profile);
  console.log(themeColor);
  console.log("profile is:",profile);
  console.log("active color is:",profile.active_theme_color);

  // Sync theme color from HOME_LIST_ITEMS_DATA on load
  useEffect(() => {
    if (identifier !== '') {
      const list_items_map = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.HOME_LIST_ITEMS_DATA));
      if (list_items_map && list_items_map[identifier]) {
        const theme_color = list_items_map[identifier].theme_color;
        if (theme_color) {
          setProfile({
            ...profile,
            active_theme_color: theme_color
          })
        }
      }
    }
  }, [identifier])

  // Auto-expand sidebar for current session
  useEffect(() => {
    if (activeSessionNo && programs.length > 0) {
      navigationData.expandParentContainers(activeSessionNo);
    }
  }, [activeSessionNo, programs.length]);

  // Position tracking effect setup
  useEffect(() => {
    const cleanup = setupPositionTracking(
      activeSessionNo,
      activeSegment,
      sessionData.currentSlideIndex,
      profile?._id,
      identifier,
      sessionContent,
      updateCurrentPosition,
      positionUpdateIntervalRef,
      lastPositionUpdateRef
    );

    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSessionNo, activeSegment, sessionData.currentSlideIndex, profile?._id, identifier, sessionContent]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (positionUpdateIntervalRef.current) {
        clearInterval(positionUpdateIntervalRef.current);
      }
    };
  }, []);

  // Helper functions
  const handleGoToHome = () => {
    history.push('/home');
  };

  const handleContinueToNextSegment = () => {
    // Only real segments are arrays of slides — metadata fields like `tier` live at the same
    // top level and must not become a pseudo-segment (its lone value would render as a fake slide).
    const segmentKeys = Object.keys(sessionContent).filter(segmentKey => Array.isArray(sessionContent[segmentKey]));
    const currentSegmentIndex = segmentKeys.indexOf(activeSegment);
    const nextSegmentKey = segmentKeys[currentSegmentIndex + 1];
    
    // Hide popup and reset state
    setShowSegmentCompletionPopup(false);
    
    // Immediate update before moving to next segment
    updateCurrentPosition(activeSessionNo, activeSegment, sessionData.currentSlideIndex, true);
    sessionData.setActiveSegment(nextSegmentKey);
    sessionData.setCurrentSlideIndex(0);
    activityData.resetActivityState();
    
    // Immediate update for new segment position
    setTimeout(() => {
      updateCurrentPosition(activeSessionNo, nextSegmentKey, 0, true);
    }, 100);
  };

  const handleContinueToNextSession = () => {
    setShowSegmentCompletionPopup(false);
    const nextSession = navigationData.findNextSession();
    if (nextSession) {
      // Capture the current session (the one being completed) BEFORE navigating
      const previousSession = activeSessionNo;
      
      // Navigate to next session, passing the previous session to mark as complete
      // handleSessionSelect will send both completion and position in one API call
      handleSessionSelect(nextSession.session_no, previousSession);
    }
  };

  const handleSessionSelectWithExpansion = (sessionNo) => {
    if (sessionNo === activeSessionNo) {
      navigationData.handleToggleSession(sessionNo);
      return;
    }
    navigationData.clearExplorationExpansions();
    handleSessionSelect(sessionNo);
    navigationData.expandParentContainers(sessionNo);
  };

  const handleSegmentSelectWithReset = (segmentKey) => {
    updateCurrentPosition(activeSessionNo, segmentKey, 0, true);
    sessionData.setActiveSegment(segmentKey);
    sessionData.setCurrentSlideIndex(0);
    activityData.resetActivityState();
  };

  // Build segments list - now segments come directly from sessionContent
  // Assessments are embedded within segments (e.g., Seg2 has pre-assessments, Seg6 has post-assessments)
  // Only real segments are arrays of slides — metadata fields like `tier` live at the same
  // top level and must not become a pseudo-segment (its lone value would render as a fake slide).
  // Sessions created before slide_content was tracked (or reached purely by
  // switching discussion tabs, without re-triggering handleDiscussionStart)
  // won't have it on the persisted session object - patch it in from the
  // live slide data here so every tab shows its description regardless of
  // when its session was created.
  const slideContentBySlideId = useMemo(() => {
    const map = {};
    if (sessionContent && typeof sessionContent === 'object') {
      Object.values(sessionContent).forEach(segmentSlides => {
        if (Array.isArray(segmentSlides)) {
          segmentSlides.forEach(slide => {
            if (slide && slide.SlideID) {
              map[slide.SlideID] = slide.SlideContent;
            }
          });
        }
      });
    }
    return map;
  }, [sessionContent]);

  // sessionContent only ever holds the CURRENTLY active course session's
  // slides - a discussion started in an earlier, already-completed session
  // won't be found in slideContentBySlideId at all. resolvedSlideContentRef
  // is filled in lazily (see effect below) by fetching that other session's
  // content on demand and searching it for the slide.
  const resolvedSlideContentRef = useRef({});
  const [resolvedSlideContentVersion, setResolvedSlideContentVersion] = useState(0);
  const sessionContentCacheRef = useRef({});
  const slideContentLookupInFlightRef = useRef({});

  const discussionSessionsWithContent = useMemo(() => {
    return (discussionData.discussionSessions || []).map(session => {
      if (session.slide_content) return session;
      const resolved = slideContentBySlideId[session.slide_id] ?? resolvedSlideContentRef.current[session.slide_id];
      return resolved ? { ...session, slide_content: resolved } : session;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discussionData.discussionSessions, slideContentBySlideId, resolvedSlideContentVersion]);

  const activeSessionWithContent = discussionSessionsWithContent[discussionData.activeTabIndex];

  // programs is a nested tree (program -> units -> sessions), not a flat list
  // of sessions - flatten it once so the cross-session lookup below can just
  // iterate session_no values directly.
  const allSessionNos = useMemo(() => {
    const nos = [];
    if (Array.isArray(programs)) {
      programs.forEach(program => {
        (program.units || []).forEach(unit => {
          (unit.sessions || []).forEach(session => {
            if (session.session_no != null) nos.push(session.session_no);
          });
        });
      });
    }
    return nos;
  }, [programs]);

  // When the visible discussion tab has no content yet, search other course
  // sessions for its slide (skipping ones already checked/cached), instead of
  // leaving the description permanently blank for sessions from the past.
  useEffect(() => {
    if (!discussionData.isModalOpen) return;
    const targetSlideId = activeSessionWithContent?.slide_id;
    if (!targetSlideId || activeSessionWithContent?.slide_content) return;
    if (resolvedSlideContentRef.current[targetSlideId]) return;
    if (slideContentLookupInFlightRef.current[targetSlideId]) return;
    if (!identifier || allSessionNos.length === 0) return;

    slideContentLookupInFlightRef.current[targetSlideId] = true;

    (async () => {
      try {
        for (const sessionNo of allSessionNos) {
          if (sessionNo === activeSessionNo) continue;

          let content = sessionContentCacheRef.current[sessionNo];
          if (!content) {
            try {
              const response = await fetchLearningProgramContent(identifier, sessionNo);
              content = response.data && response.data.content ? sortSessionContent(deriveSlideLayouts(response.data.content, sessionNo)) : {};
            } catch (fetchError) {
              console.warn(`Could not fetch session ${sessionNo} content while resolving discussion slide content:`, fetchError);
              content = {};
            }
            sessionContentCacheRef.current[sessionNo] = content;
          }

          const match = Object.values(content)
            .filter(Array.isArray)
            .flat()
            .find(slide => slide && slide.SlideID === targetSlideId);

          if (match) {
            resolvedSlideContentRef.current[targetSlideId] = match.SlideContent;
            setResolvedSlideContentVersion(v => v + 1);
            break;
          }
        }
      } finally {
        delete slideContentLookupInFlightRef.current[targetSlideId];
      }
    })();
  }, [discussionData.isModalOpen, activeSessionWithContent, identifier, allSessionNos, activeSessionNo]);

  const segmentsWithAssessments = useMemo(() => {
    if (!sessionContent) return [];
    return Object.keys(sessionContent).filter(segmentKey => Array.isArray(sessionContent[segmentKey]));
  }, [sessionContent]);

  const slidesInSegment = useMemo(() => {
    if (!sessionContent || !activeSegment) return [];
    const arr = sessionContent[activeSegment];
    return Array.isArray(arr) ? arr : [];
  }, [sessionContent, activeSegment]);

  const currentSlide = useMemo(
      () => slidesInSegment[sessionData.currentSlideIndex],
      [slidesInSegment, sessionData.currentSlideIndex]
  );

  // Helper function to get module code from active session
  const getCurrentModuleCode = useCallback(() => {
    if (!activeSessionNo || !programs || programs.length === 0) return null;

    // Find which program/module contains the current session
    for (const program of programs) {
      if (program.units) {
        for (const unit of program.units) {
          if (unit.sessions) {
            for (const session of unit.sessions) {
              if (session.session_no === activeSessionNo) {
                // Return the program code as the module code
                return program.code;
              }
            }
          }
        }
      }
    }
    return null;
  }, [activeSessionNo, programs]);

  // Get current module code
  const currentModuleCode = useMemo(() => getCurrentModuleCode(), [getCurrentModuleCode]);

  // Counts the runs of the result-slide effect below that got past its session-switch
  // guard, so a run with requests still in flight can tell whether it is still the
  // latest one before it publishes a score or clears the loading state.
  const resultSlideRunRef = useRef(0);

  // Call the finish services before showing the ASSESSMENT_RESULT slide; the slide
  // shows the score of the session it belongs to.
  useEffect(() => {
    // Session-switch race: activeSessionNo changes before the new session's content,
    // segment and slide index are in place, so a result slide is briefly paired with
    // the wrong session. Wait until the session has finished loading (which covers the
    // segment / slide-index reset) and its content belongs to the active session;
    // otherwise this effect would score the freshly-opened session and tick it as
    // completed. A run that stops here leaves the previous run's results in place.
    if (sessionLoading || loadedSessionContentRef.current !== activeSessionNo) {
      return;
    }

    const runId = ++resultSlideRunRef.current;
    const isLatestRun = () => resultSlideRunRef.current === runId;

    // A score belongs to one session's result slide: drop it whenever the slide or
    // session changes, so another session's score is never shown.
    setAssessmentScore(null);

    // If we're not on the result slide or missing identifiers
    if (currentSlide?.SlideLayout !== 'ASSESSMENT_RESULT' || !profile?._id || !identifier) {
      setAssessmentResultLoading(false);
      return;
    }

    const callFinishAssessments = async () => {
      if (currentSlide?.SlideLayout === 'ASSESSMENT_RESULT' && profile?._id && identifier) {
        setAssessmentResultLoading(true);
        try {
          // Score the active session before aggregating; its score
          // (data.session_result.score) is the number the result slide shows. A session
          // that is already scored returns its stored result. The slide-navigation
          // auto-complete intentionally skips the course's LAST session, so that session
          // relies on this call to be scored. If the call fails the slide is left
          // without a score.
          if (activeSessionNo) {
            try {
              const sessionResponse = await finishSessionAssessmentsService(identifier, profile._id, currentModuleCode, activeSessionNo);
              if (isLatestRun()) {
                setAssessmentScore(sessionResponse?.data?.session_result?.score ?? null);
              }
            } catch (sessionScoreErr) {
              console.error('Error scoring session before module aggregate:', activeSessionNo, sessionScoreErr);
            }
          }

          // Module aggregate: still called for the module result and the completion
          // tick below. Its score and progress are not shown on this slide.
          const response = await finishCourseAssessmentsService(identifier, profile._id, currentModuleCode);
          console.log('Assessment completion service called successfully with module code:', currentModuleCode, response);

          // Store assessment results
          if (response?.data?.assessment_results) {
            setAssessmentResults(response.data.assessment_results);
          }

          // The finish flow completes the module's sessions server-side (including
          // the last/single session, which the frontend deliberately defers to the
          // server). Reflect that tick locally so it appears immediately instead of
          // only after leaving and re-entering the course. Skip when scoring failed,
          // matching the server which does not mark completion in that case.
          if (!response?.data?.scoring_error && activeSessionNo) {
            markSessionCompletedLocal(activeSessionNo);
          }
        } catch (error) {
          console.error('Error calling finishCourseAssessmentsService:', error);
          if (!error.message.startsWith('NOK:')) {
            setToastMessage(error.message);
            setToastShow(true);
          } else {
            // Show error dialog instead of toast
            setErrorDialogMessage(error.message.replace('NOK:', '') || 'An error occurred while processing the assessment results. Please try again.');
            setShowErrorDialog(true);
          }
        } finally {
          if (isLatestRun()) {
            setAssessmentResultLoading(false);
          }
        }
      }
    };

    callFinishAssessments();
  }, [currentSlide, profile?._id, identifier, currentModuleCode, activeSessionNo, markSessionCompletedLocal, sessionLoading, loadedSessionContentRef]);

  const skipTitleSpeech = useMemo(() => {
    const title = currentSlide?.SlideTitle || '';
    const prevSlide = sessionData.currentSlideIndex > 0 ? slidesInSegment[sessionData.currentSlideIndex - 1] : null;
    const prevTitle = prevSlide?.SlideTitle || null;
    return prevSlide ? (prevTitle === title) : (lastSpokenSlideTitleRef.current === title);
  }, [currentSlide, slidesInSegment, sessionData.currentSlideIndex]);

  // Strip HTML tags for TTS - convert to plain text with spaces
  const stripHtmlForTTS = useCallback((text) => {
    if (typeof text !== 'string') return String(text || '');
    return text
        // Preserve boundaries between block/list tags so words don't glue together when tags are stripped:
        // "</li><li>" -> "</li> <li>"
        .replace(/<\/li>\s*<li/gi, '</li> <li')
        .replace(/<\/p>\s*<p/gi, '</p> <p')
        .replace(/<\/(dt|dd)>\s*<(dt|dd)/gi, '</$1> <$2')
        // Keep punctuation tokenization consistent with DOM parsing:
        // "</b>." -> "</b> ." so "." doesn't get attached to the last bold word
        .replace(/(<\/[^>]+>)([.,!?;:])/g, '$1 $2')
        .replace(/<br\s*\/?>/gi, ' ')          // Replace <br> with space
        .replace(/<[^>]+>/g, '')               // Remove all HTML tags
        .replace(/&nbsp;/gi, ' ')              // Replace &nbsp; with space
        .replace(/&amp;/gi, '&')               // Replace &amp; with &
        .replace(/&lt;/gi, '<')                // Replace &lt; with <
        .replace(/&gt;/gi, '>')                // Replace &gt; with >
        .replace(/&quot;/gi, '"')              // Replace &quot; with "
        .replace(/&#39;/gi, "'")                // Replace &#39; with '
        .replace(/\s+/g, ' ')                  // Collapse multiple spaces
        .trim();
  }, []);

  const prepareSpeechForSlide = useCallback((slide) => {
    speechWordsRef.current = [];
    speechPartRangesRef.current = {};
    if (!slide) return '';

    const parts = [];
    const push = (id, text) => {
      if (!text) return;
      const cleanText = stripHtmlForTTS(String(text));
      if (!cleanText) return;
      parts.push({ id, text: cleanText });
    };

    const sc = slide.SlideContent;

    // Handle ASSESSMENT_ACTIVITY slides separately - don't read title
    if (slide.SlideLayout === 'ASSESSMENT_ACTIVITY') {
      // For assessment: read all parts - subtitle_top, question, subtitle_bottom, and options
      if (sc.question_subtitle_top) {
        push('subtitle_top', sc.question_subtitle_top);
      }
      if (sc.instrument_question) {
        push('question', sc.instrument_question);
      }
      if (sc.question_subtitle_bottom) {
        push('subtitle_bottom', sc.question_subtitle_bottom);
      }
      // Parse and read options
      let options = [];
      try {
        options = typeof sc.instrument_response === 'string'
          ? JSON.parse(sc.instrument_response)
          : sc.instrument_response;
        if (!Array.isArray(options)) options = [options];
      } catch (e) {
        options = [];
      }
      options.forEach((option, idx) => {
        if (option && typeof option === 'string') {
          // Add full stop for TTS pause if not present (won't affect visual rendering)
          let optionText = option.trim();
          if (!/[.!?]$/.test(optionText)) {
            optionText += '.';
          }
          push(`option_${idx}`, optionText);
        }
      });

      // Compute word ranges and return early for assessments
      let global = 0;
      parts.forEach(p => {
        const words = p.text.match(/[^\s]+/g) || [];
        const start = global;
        const end = start + words.length - 1;
        speechPartRangesRef.current[p.id] = { start, end };
        speechWordsRef.current.push(...words);
        global = end + 1;
      });
      return parts.map(p => p.text).join(' ');
    }

    // Handle POLL_QUIZ_ACTIVITY slides - read title, content, and options
    if (slide.SlideLayout === 'POLL_QUIZ_ACTIVITY') {
      const pollTitle = resolveSlideTitle(slide);
      if (pollTitle && !skipTitleSpeech) {
        push('poll_title', pollTitle);
      }
      if (sc && typeof sc === 'string') {
        push('poll_content', sc);
      }
      // Read poll options
      if (Array.isArray(slide.Options)) {
        slide.Options.forEach((option, idx) => {
          if (option && typeof option === 'string') {
            // Add full stop for TTS pause if not present
            let optionText = option.trim();
            if (!/[.!?]$/.test(optionText)) {
              optionText += '.';
            }
            push(`poll_option_${idx}`, optionText);
          }
        });
      }

      // Compute word ranges and return early for polls
      let global = 0;
      parts.forEach(p => {
        const words = p.text.match(/[^\s]+/g) || [];
        const start = global;
        const end = start + words.length - 1;
        speechPartRangesRef.current[p.id] = { start, end };
        speechWordsRef.current.push(...words);
        global = end + 1;
      });
      return parts.map(p => p.text).join(' ');
    }

    // For all other slides, read title if not skipped
    const resolvedTitle = resolveSlideTitle(slide);
    if (resolvedTitle && !skipTitleSpeech) {
      push('title', resolvedTitle + '.');
    }

    if (typeof sc === 'string') {
      push('content_0', sc);
    } else if (slide.SlideLayout === 'PRE_ASSESSMENT_INTRO' || slide.SlideLayout === 'POST_ASSESSMENT_INTRO') {
      // Handle assessment intro slides - read title (already handled above) and content if present
      if (sc && typeof sc === 'string') {
        push('intro_content', sc);
      } else if (sc && typeof sc === 'object' && sc.content) {
        push('intro_content', sc.content);
      }
    } else if (Array.isArray(sc)) {
      sc.forEach((item, idx) => {
        if (typeof item === 'string') {
          push(`content_${idx}`, item);
        } else if (item && typeof item === 'object') {
          const titleLike = item.PointTitle ?? item.Title;
          const textLike  = item.PointText  ?? item.Text;
          if (titleLike) push(`content_${idx}_title`, titleLike + '.');
          if (textLike)  push(`content_${idx}_text`,  textLike);
        }
      });
    } else if (sc && typeof sc === 'object') {
      Object.entries(sc).forEach(([k, v]) => {
        if (typeof v === 'string') push(`content_obj_${k}`, v);
      });
    }

    // compute global word ranges for highlighting
    let global = 0;
    parts.forEach(p => {
      const words = p.text.match(/[^\s]+/g) || [];
      const start = global;
      const end = start + words.length - 1;
      speechPartRangesRef.current[p.id] = { start, end };
      speechWordsRef.current.push(...words);
      global = end + 1;
    });

    return parts.map(p => p.text).join(' ');
  }, [skipTitleSpeech, stripHtmlForTTS]);

  const speechText = useMemo(() => prepareSpeechForSlide(currentSlide), [prepareSpeechForSlide, currentSlide]);

  const renderSpokenText = useCallback((partId, raw) => {
    if (raw == null) return null;
    const text = String(raw);
    const words = text.match(/[^\s]+/g) || [];
    const range = speechPartRangesRef.current[partId];

    // If no range, still wrap words in spans (no highlighting)
    if (!range) {
      return words.map((w, i) => <span key={`${partId}-${i}`}>{w + ' '}</span>);
    }

    // With range, add highlighting using inline styles (matching DQProProgPreview)
    const {start} = range;
    return words.map((w, i) => {
      const globalIndex = start + i;
      const isCurrent = globalIndex === currentWordIndex;

      return (
        <span
          key={`${partId}-${i}`}
          style={isCurrent ? {
            backgroundColor: '#FFF3CD',
            color: '#000',
            borderRadius: '4px'
          } : {}}
        >
          {w + ' '}
        </span>
      );
    });
  }, [currentWordIndex]);


  const startSpeech = useCallback(() => {
    if (!speechText) return;

    // Check if speech synthesis is supported
    if (!(typeof window !== 'undefined' && 'speechSynthesis' in window) || !window.speechSynthesis) {
      setToastMessage(TTS_TOAST_WARNING);
      setToastShow(true);
      return;
    }

    // Cancel any existing speech
    window.speechSynthesis.cancel();

    const utter = new SpeechSynthesisUtterance(speechText);
    // Get language from URL
    const ttsLang = getTTSLanguageCode(pageLc);
    utter.lang = ttsLang;
    utter.rate = ttsRate;
    utter.pitch = ttsPitch;
    utter.volume = ttsVolume;

    // Set selected voice if chosen
    let voices;
    try {
      const vs = window.speechSynthesis?.getVoices?.() || [];
      // Filter voices for the detected language
      voices = vs.filter(voice => {
        const voiceLang = (voice.lang || '').toLowerCase();
        const targetLang = ttsLang.toLowerCase();
        return voiceLang.startsWith(targetLang.split('-')[0]); // Match base language (e.g., 'ja' from 'ja-JP')
      });
    } catch (e) {
      voices = [];
    }

    if (!voices || voices.length === 0) {
      // Some browsers load voices asynchronously
      setTimeout(() => {
        const vs = window.speechSynthesis?.getVoices?.() || [];
        const targetLang = ttsLang.toLowerCase();
        const retryVoices = vs.filter(voice => {
          const voiceLang = (voice.lang || '').toLowerCase();
          return voiceLang.startsWith(targetLang.split('-')[0]);
        });
        if (!retryVoices || retryVoices.length === 0) {
          // setToastMessage(TTS_TOAST_WARNING);
          // setToastShow(true);
          return;
        }
        // Voices now available; try again
        startSpeech();
      }, 500);
      return;
    }

    if (voices.length > 0) {
      let chosen = null;
      if (selectedVoiceURI) {
        chosen = voices.find(v => v.voiceURI === selectedVoiceURI) || null;
      }
      if (!chosen) {
        // Try to find a female voice
        const femaleVoice = voices.find(voice => {
          const name = (voice.name || '').toLowerCase();
          return (
            name.includes('female') ||
            name.includes('zira') || name.includes('susan') || name.includes('samantha') || name.includes('karen') ||
            name.includes('anna') || name.includes('marie') || name.includes('serena') || name.includes('alexia') ||
            name.includes('tessa') || name.includes('alice') || name.includes('victoria') || name.includes('celia') ||
            name.includes('amelie') || name.includes('ellen') || name.includes('kyoko') || name.includes('sayaka') ||
            name.includes('yuna') || name.includes('ayumi') || name.includes('premwadee')
          );
        });
        chosen = femaleVoice || null;
        if (!chosen) {
          if (voices.length >= 2) chosen = voices[1];
          else if (voices.length > 0) chosen = voices[0];
        }
      }
      if (chosen) utter.voice = chosen;
    }

    setSpeaking(true);
    setPaused(false);
    setCurrentWordIndex(-1);

    utter.onstart = () => {
      setSpeaking(true);
      setPaused(false);
    };

    utter.onboundary = (e) => {
      const upTo = speechText.slice(0, (e?.charIndex ?? 0));
      const count = (upTo.match(/[^\s]+/g) || []).length;
      setCurrentWordIndex(count);
    };

    utter.onend = () => {
      setSpeaking(false);
      setPaused(false);
      setCurrentWordIndex(-1);
      if (!skipTitleSpeech && currentSlide?.SlideTitle) {
        lastSpokenSlideTitleRef.current = currentSlide.SlideTitle;
      }
      if (ttsAuto && typeof onSpeechEndedRef.current === 'function') {
        onSpeechEndedRef.current();
      }
    };

    utter.onerror = () => {
      setSpeaking(false);
      setPaused(false);
      setCurrentWordIndex(-1);
      // setToastMessage(TTS_TOAST_WARNING);
      // setToastShow(true);
    };

    utteranceRef.current = utter;

    try {
      window.speechSynthesis.speak(utter);
      // Fallback check to ensure speech actually started
      setTimeout(() => {
        if (!window.speechSynthesis.speaking && !window.speechSynthesis.pending) {
          // did not start properly
          // We avoid spamming toast; this case is logged silently.
        }
      }, 250);
    } catch (error) {
      setSpeaking(false);
      setPaused(false);
      setCurrentWordIndex(-1);
      // setToastMessage(TTS_TOAST_WARNING);
      // setToastShow(true);
    }
  }, [speechText, ttsAuto, skipTitleSpeech, currentSlide, ttsRate, ttsPitch, ttsVolume, selectedVoiceURI, pageLc, setToastMessage, setToastShow, TTS_TOAST_WARNING]);

  const pauseSpeech = useCallback(() => {
    if (!speaking) return;
    if (!paused) { window.speechSynthesis.pause(); setPaused(true); }
    else { window.speechSynthesis.resume(); setPaused(false); }
  }, [speaking, paused]);

  const stopSpeech = useCallback(() => {
    window.speechSynthesis.cancel();
    setSpeaking(false); setPaused(false); setCurrentWordIndex(-1);
  }, []);

  const onSpeechEndedRef = useRef(null);

  // cancel on unmount
  useEffect(() => () => window.speechSynthesis.cancel(), []);

  // Load available voices (and refresh when they become available)
  useEffect(() => {
    const loadVoices = () => {
      try {
        const vs = window.speechSynthesis?.getVoices?.() || [];
        const ttsLang = getTTSLanguageCode(pageLc);
        const baseLang = ttsLang.split('-')[0].toLowerCase(); // e.g., 'ja' from 'ja-JP'

        // Filter voices for the current language
        const filteredVoices = vs.filter(voice => {
          const voiceLang = (voice.lang || '').toLowerCase();
          return voiceLang.startsWith(baseLang);
        });

        setAvailableVoices(filteredVoices);
      } catch (e) {
        setAvailableVoices([]);
      }
    };
    loadVoices();
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.addEventListener('voiceschanged', loadVoices);
    }
    return () => {
      if (typeof window !== 'undefined' && window.speechSynthesis) {
        window.speechSynthesis.removeEventListener('voiceschanged', loadVoices);
      }
    };
  }, [pageLc]);

  // If Auto is on, restart when slide changes; if off, stop.
  useEffect(() => {
    if (!slidesInSegment.length) return;
    if (ttsAuto) {
      stopSpeech();
      startSpeech();
    } else {
      stopSpeech();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ttsAuto, activeSegment, sessionData.currentSlideIndex, speechText]);

  // When settings change while speaking, restart to apply new settings
  useEffect(() => {
    if (speaking) {
      stopSpeech();
      startSpeech();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ttsRate, ttsPitch, ttsVolume, selectedVoiceURI]);


  // Loading state
  if (loading) {
    return (
      <S.LoadingContainer>
        <S.Spinner />
        <p style={{fontFamily: `"Source Sans Pro", "Helvetica Neue"`, fontSize:'18px'}}>{GET_APP_LOCALIZE_TEXT(pageLc, 'ct__loading_course')}...</p>
      </S.LoadingContainer>
    );
  }

  // Calculate state for render
  // Only real segments are arrays of slides — metadata fields like `tier` live at the same
  // top level and must not become a pseudo-segment (its lone value would render as a fake slide).
  const segmentKeys = sessionContent && typeof sessionContent === 'object'
    ? Object.keys(sessionContent).filter(segmentKey => Array.isArray(sessionContent[segmentKey]))
    : [];
  const currentSegmentIndex = segmentKeys.indexOf(activeSegment);
  const nextSegmentKey = segmentKeys[currentSegmentIndex + 1];

  const { isLastSlideOfSegment, isLastSegment, isLastSession } = navigationData.checkLastSlideStatus();
  const nextSession = (isLastSegment && isLastSlideOfSegment) ? navigationData.findNextSession() : null;

  const isEndOfSession = isLastSlideOfSegment && isLastSegment;
  const isEndOfCourse = isEndOfSession && isLastSession;
  return (
    <div style={{display:'flex', minHeight: '100vh',justifyContent:'space-between', flexDirection:'column'}}>
      <S.Layout>
        <S.TTSHighlightStyles />
        <S.SlideContentGlobalStyles />
        <CourseSidebar
          themeColor={themeColor}
          collapsed={navigationData.collapsed}
          toggleSider={navigationData.toggleSider}
          layoutLoading={layoutLoading}
          courseTitle={courseTitle}
          programs={programs}
          isProgramExpanded={navigationData.isProgramExpanded}
          isUnitExpanded={navigationData.isUnitExpanded}
          isSessionExpanded={navigationData.isSessionExpanded}
          activeSessionNo={activeSessionNo}
          activeSegment={activeSegment}
          sessionContent={sessionContent}
          onToggleProgram={navigationData.handleToggleProgram}
          onToggleUnit={navigationData.handleToggleUnit}
          onToggleSession={navigationData.handleToggleSession}
          onSessionSelect={handleSessionSelectWithExpansion}
          onSegmentSelect={handleSegmentSelectWithReset}
          getSegmentLabel={(key) => getSegmentLabel(key, sloTitles, pageLc)}
          resetActivityState={activityData.resetActivityState}
          updateCurrentPosition={updateCurrentPosition}
          segmentsWithAssessments={segmentsWithAssessments}
          pageLc={pageLc}
        />
        <S.Main>
          <S.Header>
            <S.ToggleButton
              themeColor={themeColor}
              onClick={navigationData.toggleSider}
              aria-label={navigationData.collapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {navigationData.collapsed ? "≫" : "≪"}
            </S.ToggleButton>
            <S.SessionTitle themeColor={themeColor}>
              {navigationData.findSessionName()}
            </S.SessionTitle>
            {positionUpdateError && (
              <S.ErrorMessage>
                {positionUpdateError}
              </S.ErrorMessage>
            )}
          </S.Header>
          
          {sessionContent && typeof sessionContent === 'object' && Object.values(sessionContent).some(arr => Array.isArray(arr) && arr.length > 0) && (
            <ProgressBar
              sessionContent={sessionContent}
              activeSegment={activeSegment}
              currentSlideIndex={sessionData.currentSlideIndex}
              profile={profile}
              themeColor={themeColor}
              isLastSlideOfSegment={isLastSlideOfSegment}
              isEndOfCourse={isEndOfCourse}
              nextSegmentKey={nextSegmentKey}
              nextSegmentLabel={nextSegmentKey ? getSegmentLabel(nextSegmentKey, sloTitles, pageLc) : null}
              nextSession={nextSession}
              endOfSegmentRef={navigationData.endOfSegmentRef}
              onContinueToNextSegment={handleContinueToNextSegment}
              onGoToHome={handleGoToHome}
              onContinueToNextSession={handleContinueToNextSession}
              segmentIndex={currentSegmentIndex}
              activityCompleted={activityData.activityCompleted}
              isActivityRequired={activityData.isActivityRequired}
              showSegmentCompletionPopup={showSegmentCompletionPopup}
              onClosePopup={() => setShowSegmentCompletionPopup(false)}
              sidebarCollapsed={navigationData.collapsed}
              pageLc={pageLc}
              hideBar={currentSlide?.SlideLayout === 'ASSESSMENT_ACTIVITY'
                || currentSlide?.SlideLayout === 'PRE_ASSESSMENT_INTRO'
                || currentSlide?.SlideLayout === 'POST_ASSESSMENT_INTRO'}
            />
          )}

          <S.Content>
            {sessionLoading ? (
              <S.ContentLoading>
                <S.Spinner />
                <p>{GET_APP_LOCALIZE_TEXT(pageLc, 'ct__loading_session')}...</p>
              </S.ContentLoading>
            ) : sessionContent && typeof sessionContent === 'object' && Object.values(sessionContent).some(arr => Array.isArray(arr) && arr.length > 0) ? (
              <SlideViewer
                sessionContent={sessionContent}
                activeSegment={activeSegment}
                currentSlideIndex={sessionData.currentSlideIndex}
                collapsed={navigationData.collapsed}
                pageLc={pageLc}
                onPrevSlide={navigationData.goToPrevSlide}
                onNextSlide={navigationData.goToNextSlide}
                isNextButtonDisabled={isNextButtonDisabled()}
                journalingText={activityData.journalingText}
                setJournalingText={activityData.setJournalingText}
                shareWorkLink={activityData.shareWorkLink}
                setShareWorkLink={activityData.setShareWorkLink}
                selectedPollOption={activityData.selectedPollOption}
                selectedPollOptions={activityData.selectedPollOptions}
                setSelectedPollOption={activityData.setSelectedPollOption}
                setSelectedPollOptions={activityData.setSelectedPollOptions}
                activityCompleted={activityData.activityCompleted}
                interactionLoading={activityData.interactionLoading}
                fetchingExistingRecord={activityData.fetchingExistingRecord}
                inputsDisabled={activityData.inputsDisabled}
                onJournalingSave={activityData.handleJournalingSave}
                onShareWorkSubmit={activityData.handleShareWorkSubmit}
                onPollSubmit={activityData.handlePollSubmit}
                onAssessmentSubmit={activityData.handleAssessmentSubmit}
                onDiscussionStart={activityData.handleDiscussionStart}
                onEditActivity={activityData.handleEditActivity}
                assessmentAnswers={activityData.assessmentAnswers}
                setAssessmentAnswers={activityData.setAssessmentAnswers}
                module_code={identifier}
                session={activeSessionNo}
                segment={activeSegment}
                assessmentScore={assessmentScore}
                assessmentResultLoading={assessmentResultLoading}
                // TTS wiring
                speaking={speaking}
                paused={paused}
                ttsAuto={ttsAuto}
                setTtsAuto={setTtsAuto}
                startSpeech={startSpeech}
                pauseSpeech={pauseSpeech}
                stopSpeech={stopSpeech}
                registerOnSpeechEnded={(fn) => { onSpeechEndedRef.current = fn; }}
                // highlight helpers for SlideRenderer
                renderSpokenText={renderSpokenText}
                skipTitleSpeech={skipTitleSpeech}
                // TTS settings
                ttsRate={ttsRate}
                setTtsRate={setTtsRate}
                ttsPitch={ttsPitch}
                setTtsPitch={setTtsPitch}
                ttsVolume={ttsVolume}
                setTtsVolume={setTtsVolume}
                availableVoices={availableVoices}
                selectedVoiceURI={selectedVoiceURI}
                setSelectedVoiceURI={setSelectedVoiceURI}
                themeColor={themeColor}
              />
            ) : (
              <S.NoContent>
                <p>{GET_APP_LOCALIZE_TEXT(pageLc, 'ct__no_content_available')}</p>
              </S.NoContent>
            )}
          </S.Content>
        </S.Main>
      </S.Layout>
      
      {/* Discussion Feature */}
      <FloatingChatButton
        onClick={discussionData.openModal}
        disabled={loading || !identifier}
        themeColor={themeColor}
        unreadCount={discussionData.unreadCount}
        pageLc={pageLc}
      />

      {/* Activity Widget Feature - Only show when feature flag is enabled */}
      {FEATURE_FLAGS.ACTIVITY_WIDGET_ENABLED && (
        <>
          <FloatingActivityButton
            onClick={activityWidgetData.openModal}
            disabled={loading || !identifier}
            activityCount={activityWidgetData.userActivities.length}
            pageLc={pageLc}
          />
          
          <ActivityModal
            isOpen={activityWidgetData.isModalOpen}
            onClose={activityWidgetData.closeModal}
            courseTitle={courseTitle || "Course Activities"}
            userActivities={activityWidgetData.userActivities}
            activeTabIndex={activityWidgetData.activeTabIndex}
            onTabChange={activityWidgetData.handleTabChange}
            getCurrentActivity={activityWidgetData.getCurrentActivity}
            getCurrentActivityResponses={activityWidgetData.getCurrentActivityResponses}
            isCurrentActivityLoading={activityWidgetData.isCurrentActivityLoading}
            formatActivityType={activityWidgetData.formatActivityType}
            formatTimestamp={activityWidgetData.formatTimestamp}
            loading={activityWidgetData.loading}
            error={activityWidgetData.error}
            themeColor={themeColor}
            pageLc={pageLc}
          />
        </>
      )}
      
      <DiscussionModal
        isOpen={discussionData.isModalOpen}
        onClose={discussionData.closeModal}
        messages={discussionData.messages}
        loading={discussionData.loading}
        error={discussionData.error}
        newMessage={discussionData.newMessage}
        onMessageChange={discussionData.handleMessageChange}
        onKeyPress={discussionData.handleKeyPress}
        onSendMessage={discussionData.postMessage}
        posting={discussionData.posting}
        canSend={discussionData.canSend}
        characterCount={discussionData.characterCount}
        isOverLimit={discussionData.isOverLimit}
        maxLength={discussionData.maxLength}
        formatMessageTime={discussionData.formatMessageTime}
        getUserInitials={discussionData.getUserInitials}
        messagesEndRef={discussionData.messagesEndRef}
        inputRef={discussionData.inputRef}
        onRefresh={discussionData.refreshMessages}
        courseTitle={courseTitle || "Course Discussion"}
        themeColor={themeColor}
        currentProfile={profile}
        // Tab support props
        discussionSessions={discussionSessionsWithContent}
        activeTabIndex={discussionData.activeTabIndex}
        activeSession={activeSessionWithContent}
        onTabSwitch={discussionData.switchToTab}
        // Edit/Delete message props
        onUpdateMessage={discussionData.updateMessage}
        onDeleteMessage={discussionData.deleteMessage}
        pageLc={pageLc}
      />

      <ErrorDialog
        isOpen={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        errorMessage={errorDialogMessage}
        title="Missing Assessment Responses"
      />
    </div>
  );
        };

        export default withRouter(CourseTime);
